import { Head, Link } from "@inertiajs/react"
import { AlertTriangle, Pencil } from "lucide-react"
import { useState } from "react"

import {
  CarStatusBadge,
  QualDueBadge,
  VerificationBadge,
  WaitingOnBadge,
  WorkOrderStatusBadge,
  WorkOrderTypeChip,
} from "@/components/products/fleet/badges"
import {
  EMPTY,
  formatDate,
  formatDateTime,
  formatDays,
  formatNumber,
  humanize,
} from "@/components/products/fleet/format"
import {
  type FleetTab,
  TabBar,
  TabPanel,
} from "@/components/products/fleet/tab-bar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { formatMoney } from "@/lib/money-format"
import type { BreadcrumbItem } from "@/types"

interface CarProps {
  id: number
  mark_number: string
  reporting_mark: string
  car_number: string
  fleet_code: string | null
  car_type: string | null
  status: string
  stencil_spec: string | null
  year_built: number | null
  capacity_gallons: number | null
  gross_rail_load_lbs: number | null
  load_limit_lbs: number | null
  tare_weight_lbs: number | null
  allowable_miles: number | null
  components: Record<string, unknown>
  next_qual_due_on: string | null
  notes: string | null
}

interface QualItem {
  id: number
  item_name: string
  due_on: string | null
  last_completed_on: string | null
  interval_years: number | null
  notes: string | null
  overdue: boolean
}

interface WorkOrderRow {
  id: number
  type: string
  status: string
  opened_on: string | null
  bad_order_date: string | null
  closed_on: string | null
  closed: boolean
  waiting_on: string | null
  days_waiting: number | null
  cost_cents: number | null
  defect_category: string | null
  shop: string | null
  facility: string | null
  mru_team: string | null
}

interface DocumentRow {
  id: number
  doc_type: string
  filename: string | null
  source: string
  review_status: string
  verification_result: Record<string, unknown> | null
  created_at: string
}

interface HistoryRow {
  id: number
  field_name: string
  from_value: string | null
  to_value: string | null
  changed_by: string | null
  created_at: string
}

interface Props {
  car: CarProps
  lessor: { id: number; name: string } | null
  rider: {
    id: number
    rider_number: string
    effective_on: string | null
    expiration_on: string | null
    monthly_rate_cents: number | null
    car_count: number | null
    status: string
    extracted_terms: Record<string, unknown>
  } | null
  facility: {
    id: number
    name: string
    city: string | null
    state: string | null
  } | null
  qual_items: QualItem[]
  work_orders: WorkOrderRow[]
  documents: DocumentRow[]
  history: HistoryRow[]
  requal_conflict: boolean
  can_edit: boolean
}

export default function CarShow({
  car,
  lessor,
  rider,
  facility,
  qual_items,
  work_orders,
  documents,
  history,
  requal_conflict,
  can_edit,
}: Props) {
  const routes = useOrgRoutes()
  const [tab, setTab] = useState("profile")

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Cars", href: routes.carsPath() },
    { title: car.mark_number, href: routes.carPath(car.id) },
  ]

  const tabs: FleetTab[] = [
    { key: "profile", label: "Profile" },
    { key: "qual", label: "Qual map", count: qual_items.length },
    { key: "work_orders", label: "Work orders", count: work_orders.length },
    { key: "documents", label: "Documents", count: documents.length },
    { key: "history", label: "History", count: history.length },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={car.mark_number} />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">
                {car.mark_number}
              </h1>
              <CarStatusBadge status={car.status} />
              {car.fleet_code && (
                <Badge variant="secondary">{car.fleet_code}</Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {humanize(car.car_type)}
              {facility && ` · ${facility.name}`}
              {lessor && (
                <>
                  {" · "}
                  <Link
                    href={routes.lessorPath(lessor.id)}
                    className="hover:underline"
                  >
                    {lessor.name}
                  </Link>
                  {rider && ` (${rider.rider_number})`}
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-right">
              <p className="text-muted-foreground text-xs">Next qual due</p>
              <QualDueBadge dueOn={car.next_qual_due_on} />
            </div>
            {can_edit && (
              <Button variant="outline" size="sm" asChild>
                <Link href={routes.editCarPath(car.id)}>
                  <Pencil className="mr-1 size-4" /> Edit
                </Link>
              </Button>
            )}
          </div>
        </div>

        {requal_conflict && <ConflictBanner rider={rider} car={car} />}

        <div>
          <TabBar
            tabs={tabs}
            active={tab}
            onChange={setTab}
            label={`${car.mark_number} details`}
          />

          <TabPanel tabKey="profile" active={tab}>
            <ProfilePanel car={car} rider={rider} />
          </TabPanel>

          <TabPanel tabKey="qual" active={tab}>
            <QualPanel items={qual_items} />
          </TabPanel>

          <TabPanel tabKey="work_orders" active={tab}>
            <WorkOrdersPanel workOrders={work_orders} />
          </TabPanel>

          <TabPanel tabKey="documents" active={tab}>
            <DocumentsPanel documents={documents} />
          </TabPanel>

          <TabPanel tabKey="history" active={tab}>
            <HistoryPanel history={history} />
          </TabPanel>
        </div>
      </div>
    </AppLayout>
  )
}

function ConflictBanner({
  rider,
  car,
}: {
  rider: Props["rider"]
  car: CarProps
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-red-500/50 bg-red-500/5 p-4">
      <AlertTriangle
        className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400"
        aria-hidden="true"
      />
      <div className="text-sm">
        <p className="font-medium text-red-700 dark:text-red-300">
          Lease expires before requal — review before testing.
        </p>
        <p className="text-muted-foreground mt-1">
          {rider
            ? `Rider ${rider.rider_number} expires ${formatDate(rider.expiration_on)}, but this car's next qualification is not due until ${formatDate(car.next_qual_due_on)}.`
            : "This car's lease ends before its next qualification is due."}{" "}
          Testing it buys compliance the lease will never use — consider
          returning the car instead.
        </p>
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="mt-0.5 text-sm">{value ?? EMPTY}</dd>
    </div>
  )
}

function ProfilePanel({
  car,
  rider,
}: {
  car: CarProps
  rider: Props["rider"]
}) {
  const componentEntries = Object.entries(car.components ?? {})

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Lease</CardTitle>
        </CardHeader>
        <CardContent>
          {rider ? (
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Rider" value={rider.rider_number} />
              <Field label="Status" value={humanize(rider.status)} />
              <Field label="Effective" value={formatDate(rider.effective_on)} />
              <Field label="Expires" value={formatDate(rider.expiration_on)} />
              <Field
                label="Monthly rate"
                value={formatMoney(rider.monthly_rate_cents)}
              />
              <Field
                label="Cars on rider"
                value={formatNumber(rider.car_count)}
              />
              {typeof rider.extracted_terms.allowable_miles === "number" && (
                <Field
                  label="Allowable miles"
                  value={`${formatNumber(
                    rider.extracted_terms.allowable_miles,
                  )} mi`}
                />
              )}
              {typeof rider.extracted_terms.mileage_penalty === "string" && (
                <Field
                  label="Mileage penalty"
                  value={rider.extracted_terms.mileage_penalty}
                />
              )}
            </dl>
          ) : (
            <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
              No rider on this car. Attach one to plan its requal against a
              lease end date.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Capacities &amp; limits</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Stencil spec" value={car.stencil_spec ?? EMPTY} />
            <Field label="Year built" value={car.year_built ?? EMPTY} />
            <Field
              label="Capacity"
              value={
                car.capacity_gallons
                  ? `${formatNumber(car.capacity_gallons)} gal`
                  : EMPTY
              }
            />
            <Field
              label="Allowable miles"
              value={formatNumber(car.allowable_miles)}
            />
            <Field
              label="Gross rail load"
              value={
                car.gross_rail_load_lbs
                  ? `${formatNumber(car.gross_rail_load_lbs)} lb`
                  : EMPTY
              }
            />
            <Field
              label="Load limit"
              value={
                car.load_limit_lbs
                  ? `${formatNumber(car.load_limit_lbs)} lb`
                  : EMPTY
              }
            />
            <Field
              label="Tare weight"
              value={
                car.tare_weight_lbs
                  ? `${formatNumber(car.tare_weight_lbs)} lb`
                  : EMPTY
              }
            />
          </dl>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Mechanical components</CardTitle>
        </CardHeader>
        <CardContent>
          {componentEntries.length === 0 ? (
            <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
              No component sheet recorded — valves, gaskets and fittings for
              this car have not been captured yet.
            </p>
          ) : (
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {componentEntries.map(([key, value]) => (
                <Field
                  key={key}
                  label={humanize(key)}
                  value={
                    typeof value === "object" && value !== null
                      ? JSON.stringify(value)
                      : String(value ?? EMPTY)
                  }
                />
              ))}
            </dl>
          )}
        </CardContent>
      </Card>

      {car.notes && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{car.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function QualPanel({ items }: { items: QualItem[] }) {
  // The controller orders by due date, so the first row IS the next one due.
  const nextDueId = items.find((item) => item.due_on)?.id

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Last completed</TableHead>
            <TableHead>Interval</TableHead>
            <TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-muted-foreground py-8 text-sm"
              >
                No qualification items on this car yet. Add them on the edit
                page so the car appears in the requal program.
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow
                key={item.id}
                className={item.id === nextDueId ? "bg-muted/50" : undefined}
              >
                <TableCell className="font-medium">
                  {item.item_name}
                  {item.id === nextDueId && (
                    <span className="text-muted-foreground ml-2 text-xs">
                      next due
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <QualDueBadge dueOn={item.due_on} />
                </TableCell>
                <TableCell className="text-sm">
                  {formatDate(item.last_completed_on)}
                </TableCell>
                <TableCell className="text-sm tabular-nums">
                  {item.interval_years ? `${item.interval_years} yr` : EMPTY}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {item.notes ?? EMPTY}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function WorkOrdersPanel({ workOrders }: { workOrders: WorkOrderRow[] }) {
  const routes = useOrgRoutes()

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm">
        Every work order this car has ever carried, newest first. Nothing here
        is ever deleted — the history is what the fleet is worth.
      </p>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead>Closed</TableHead>
              <TableHead>Waiting on</TableHead>
              <TableHead>Where</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workOrders.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-muted-foreground py-8 text-sm"
                >
                  This car has never been written up. That is a good thing.
                </TableCell>
              </TableRow>
            ) : (
              workOrders.map((wo) => (
                <TableRow key={wo.id}>
                  <TableCell>
                    <Link
                      href={routes.workOrderPath(wo.id)}
                      className="hover:underline"
                    >
                      <WorkOrderTypeChip type={wo.type} />
                    </Link>
                  </TableCell>
                  <TableCell>
                    <WorkOrderStatusBadge
                      status={wo.status}
                      closed={wo.closed}
                    />
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(wo.bad_order_date ?? wo.opened_on)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(wo.closed_on)}
                  </TableCell>
                  <TableCell>
                    {wo.closed ? (
                      <span className="text-muted-foreground text-sm">
                        {EMPTY}
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <WaitingOnBadge waitingOn={wo.waiting_on} />
                        <span className="text-muted-foreground text-xs tabular-nums">
                          {formatDays(wo.days_waiting)}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {wo.shop ?? wo.mru_team ?? wo.facility ?? EMPTY}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatMoney(wo.cost_cents)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function DocumentsPanel({ documents }: { documents: DocumentRow[] }) {
  if (documents.length === 0) {
    return (
      <div className="rounded-lg border border-dashed px-5 py-12 text-center">
        <h2 className="font-medium">No documents on this car</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          MRU reports, release forms and shop estimates land here once they
          arrive by email or upload.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Document</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Review</TableHead>
            <TableHead>Received</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc) => (
            <TableRow key={doc.id}>
              <TableCell className="font-medium">
                {doc.filename ?? EMPTY}
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="text-xs">
                  {humanize(doc.doc_type)}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">{humanize(doc.source)}</TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-1">
                  <Badge variant="outline" className="text-xs">
                    {humanize(doc.review_status)}
                  </Badge>
                  <VerificationBadge result={doc.verification_result} />
                </div>
              </TableCell>
              <TableCell className="text-sm">
                {formatDate(doc.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function HistoryPanel({ history }: { history: HistoryRow[] }) {
  if (history.length === 0) {
    return (
      <div className="rounded-lg border border-dashed px-5 py-12 text-center">
        <h2 className="font-medium">Nothing has changed yet</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Status changes on this car are recorded here as they happen.
        </p>
      </div>
    )
  }

  return (
    <ul className="divide-border divide-y rounded-md border">
      {history.map((row) => (
        <li
          key={row.id}
          className="flex flex-wrap items-baseline gap-2 p-3 text-sm"
        >
          <span className="font-medium">{humanize(row.field_name)}</span>
          <span className="text-muted-foreground">
            {row.from_value ? humanize(row.from_value) : "(unset)"} &rarr;{" "}
            {row.to_value ? humanize(row.to_value) : "(unset)"}
          </span>
          <span className="text-muted-foreground ml-auto text-xs">
            {formatDateTime(row.created_at)}
            {row.changed_by && ` · ${row.changed_by}`}
          </span>
        </li>
      ))}
    </ul>
  )
}
